import { Box, Button, Chip, CircularProgress, Divider, IconButton, TextField, Typography, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import Fetch from "../services/Fetch";
import { AsyncPaginate } from "react-select-async-paginate";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { FormattedMessage } from "react-intl";
import { useConstants } from "../hooks/UseConstants";

function CoursesFilter({ onClickClose, onClickConfirm, categoriesValue, setCategoriesValue, setCategory, pathsValue, setPathsValue, setPath, fromCount, setFromCount, toCount, setToCount, fromDate, setFromDate, toDate, setToDate, filterWait, setFilterWait }) {
    const { language, host } = useConstants();
    const theme = useTheme();
    const isAr = language === "ar";

    const resetFilter = () => {
        setCategory('');
        setPath('');
        setFromCount('');
        setToCount('');
        setFromDate('');
        setToDate('');
        setCategoriesValue(null);
        setPathsValue(null);
    };

    const loadCategories = async (search, _loadedOptions, { page }) => {
        const response = await Fetch(host + `/categories?page=${page}${search ? `&search=${encodeURIComponent(search)}` : ""}`);
        const items = response?.data?.data?.data || [];
        const pagination = response?.data?.data || {};
        const optionsFromApi = items.map((item) => ({
            value: item.id, label: language === 'en' ? item.name_en : item.name_ar,
        }));
        return {
            options: [{ value: '', label: isAr ? 'الكل' : 'All' }, ...optionsFromApi],
            hasMore: (pagination.current_page || 1) * (pagination.per_page || items.length || 1) < (pagination.total || items.length || 0),
            additional: { page: page + 1 },
        };
    };

    const loadPaths = async (search, _loadedOptions, { page }) => {
        const response = await Fetch(host + `/paths?page=${page}${search ? `&search=${encodeURIComponent(search)}` : ""}`);
        const items = response?.data?.data || [];
        const pagination = response?.data?.pagination || {};

        const optionsFromApi = items.map((item) => ({
            value: item.id, label: language === 'en' ? item.name_en : item.name_ar,
        }));
        return {
            options: [{ value: '', label: isAr ? 'الكل' : 'All' }, ...optionsFromApi],
            hasMore: (pagination.current_page || 1) * (pagination.per_page || items.length || 1) < (pagination.total || items.length || 0),
            additional: { page: page + 1 },
        };
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.paper,
                width: { xs: "92%", sm: "82%", md: "60%" },
                maxWidth: 780,
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(13,53,140,0.18)",
            }}
            dir={isAr ? 'rtl' : 'ltr'}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    background: "linear-gradient(135deg, #0D358C 0%, #2563eb 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <FilterAltOutlinedIcon sx={{ fontSize: 30 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={800}><FormattedMessage id='filtering_courses' /></Typography>
                        <Typography variant="body2" sx={{ opacity: 0.88 }}>
                            {isAr ? "فلترة الدورات حسب التصنيف والمسار وعدد الملفات وتاريخ النشر." : "Filter courses by category, path, file count, and publication date."}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClickClose} sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    <Chip label={isAr ? "فلترة متقدمة" : "Advanced filter"} color="primary" variant="outlined" />
                    <Chip label={isAr ? "تصنيف + مسار + ملفات + تاريخ" : "Category + path + files + date"} variant="outlined" />
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
                    <Box>
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75 }}><FormattedMessage id='category' /></Typography>
                        <AsyncPaginate
                            value={categoriesValue || null}
                            loadOptions={loadCategories}
                            onChange={(option) => { setCategoriesValue(option || null); setCategory(option?.value || ''); }}
                            additional={{ page: 1 }}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: 40,
                                    borderRadius: 8,
                                    borderColor: "#cbd5e1",
                                }),
                                option: (provided) => ({
                                    ...provided,
                                    color: 'black',
                                }),
                            }}
                            isSearchable
                            placeholder={isAr ? 'اختر التصنيف' : 'Select category'}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75 }}><FormattedMessage id='education_path' /></Typography>
                        <AsyncPaginate
                            value={pathsValue || null}
                            loadOptions={loadPaths}
                            onChange={(option) => { setPathsValue(option || null); setPath(option?.value || ''); }}
                            additional={{ page: 1 }}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: 40,
                                    borderRadius: 8,
                                    borderColor: "#cbd5e1",
                                }),
                                option: (provided) => ({
                                    ...provided,
                                    color: 'black',
                                }),
                            }}
                            isSearchable
                            placeholder={isAr ? 'اختر المسار' : 'Select path'}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
                    <TextField value={fromCount} onChange={(e) => setFromCount(e.target.value)} type="number" size="small" fullWidth label={<FormattedMessage id='from' />} />
                    <TextField value={toCount} onChange={(e) => setToCount(e.target.value)} type="number" size="small" fullWidth label={<FormattedMessage id='to' />} />
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
                    <TextField value={fromDate} onChange={(e) => setFromDate(e.target.value)} type="date" size="small" fullWidth label={<FormattedMessage id='from' />} InputLabelProps={{ shrink: true }} />
                    <TextField value={toDate} onChange={(e) => setToDate(e.target.value)} type="date" size="small" fullWidth label={<FormattedMessage id='to' />} InputLabelProps={{ shrink: true }} />
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end" }}>
                    <Button variant="outlined" disabled={filterWait} sx={{ minWidth: 140 }} onClick={resetFilter}>
                        <FormattedMessage id='reset' />
                    </Button>
                    <Button
                        variant="contained"
                        disabled={filterWait}
                        sx={{ minWidth: 170, bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                        onClick={() => { setFilterWait(true); onClickConfirm(); }}
                        startIcon={filterWait ? <CircularProgress size={18} color="inherit" /> : <FilterAltOutlinedIcon />}
                    >
                        {filterWait ? (isAr ? "جاري التطبيق..." : "Applying...") : <FormattedMessage id='filtering' />}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default CoursesFilter;